import React, { useState, useEffect } from 'react';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import Footer from "../../Footer/Footer";
import { Table, Button, Container, Row, Col, Card, Badge, Modal, Pagination } from 'react-bootstrap';
import axios from 'axios';
import BaseUrl from '../../ApiServices/BaseUrl';
import './AgencyEnquiryDetail.css';

function AgencyEnquiryDetail() {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userId = user?.id;

    useEffect(() => {
        if (userId) {
            fetchEnquiries();
        } else {
            setLoading(false);
            setError("User not logged in");
        }
    }, [userId]);

    const fetchEnquiries = async () => {
        try {
            const response = await axios.get(`${BaseUrl}/api/enquiries/agency/${userId}`);
            setEnquiries(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return 'N/A';
        }
    };

    const handleViewEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEnquiry(null);
    };

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEnquiries = enquiries.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(enquiries.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    if (loading) return (
        <>
            <AgencyNavbar />
            <div className="text-center" style={{ marginTop: '150px' }}>Loading enquiries...</div>
        </>
    );

    if (error) return (
        <>
            <AgencyNavbar />
            <div className="text-center text-danger" style={{ marginTop: '150px' }}>Error: {error}</div>
        </>
    );

    return (
        <>
            <AgencyNavbar />
            <Container className="agency-enquiry-container" style={{ marginTop: '100px', marginBottom: '50px' }}>
                <Row className="mb-4">
                    <Col>
                        <h2 className="fw-bold">My Enquiries</h2>
                        <p className="text-muted">Manage user enquiries sent to your agency</p>
                    </Col>
                </Row>

                <Card className="shadow-sm border-0">
                    <Card.Body>
                        <div className="table-responsive">
                            <Table striped hover className="align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th>S.No</th>
                                        <th>User Name</th>
                                        <th>User Type</th>
                                        <th>Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentEnquiries.map((enquiry, index) => (
                                        <tr key={enquiry.id}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>
                                                <div className="fw-bold">{enquiry.name}</div>
                                                <small className="text-muted">{enquiry.email}</small>
                                            </td>
                                            <td>
                                                <Badge bg={enquiry.user_type === 'customer' ? 'primary' : 'info'} className="text-capitalize">
                                                    {enquiry.user_type}
                                                </Badge>
                                            </td>
                                            <td>{formatDate(enquiry.created_at)}</td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleViewEnquiry(enquiry)}
                                                >
                                                    View Message
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {enquiries.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">No enquiries found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center mt-4">
                                <Pagination>
                                    <Pagination.Prev
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    />
                                    {[...Array(totalPages).keys()].map(number => (
                                        <Pagination.Item
                                            key={number + 1}
                                            active={number + 1 === currentPage}
                                            onClick={() => handlePageChange(number + 1)}
                                        >
                                            {number + 1}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* Modal for viewing message */}
                <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                    <Modal.Header closeButton className="bg-light">
                        <Modal.Title>Enquiry Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {selectedEnquiry && (
                            <div>
                                {selectedEnquiry.contact_hidden && (
                                    <div className="alert alert-warning mb-4" role="alert">
                                        <strong>🔒 Contact Details Hidden</strong>
                                        <p className="mb-0 mt-2">Upgrade your subscription to view full contact details and connect with enquirers.</p>
                                        <a href="/a-subscriptionplans" className="btn btn-sm btn-warning mt-2">View Plans</a>
                                    </div>
                                )}
                                <Row className="mb-4">
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small fw-bold text-uppercase">Sender Name</label>
                                            <div className="fs-5">{selectedEnquiry.name}</div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="text-muted small fw-bold text-uppercase">User Type</label>
                                            <div className="text-capitalize">{selectedEnquiry.user_type}</div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small fw-bold text-uppercase">Email Address</label>
                                            <div>{selectedEnquiry.email}</div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="text-muted small fw-bold text-uppercase">Phone Number</label>
                                            <div>{selectedEnquiry.phone_number}</div>
                                        </div>
                                    </Col>
                                </Row>
                                <div className="mb-4">
                                    <label className="text-muted small fw-bold text-uppercase mb-2 d-block">Message</label>
                                    <div className="p-3 bg-light rounded border" style={{ whiteSpace: 'pre-wrap', minHeight: '100px' }}>
                                        {selectedEnquiry.message}
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        className="flex-grow-1"
                                        onClick={() => window.location.href = `mailto:${selectedEnquiry.email}`}
                                    >
                                        Reply via Email
                                    </Button>
                                    <Button
                                        variant="success"
                                        className="flex-grow-1"
                                        onClick={() => {
                                            const cleanNum = (selectedEnquiry.phone_number || "").replace(/\D/g, '');
                                            if (cleanNum) {
                                                window.open(`https://wa.me/${cleanNum}`, '_blank');
                                            } else {
                                                alert("Phone number not available");
                                            }
                                        }}
                                    >
                                        Reply via WhatsApp
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
                    </Modal.Footer>
                </Modal>
            </Container>
            <Footer />
        </>
    );
}

export default AgencyEnquiryDetail;
